// @flow
import { useEffect, useState } from 'react'
import {
  getArticleTeaserData,
  ArticleData,
} from '@ta-interaktiv/react-article-teasers'
import styled from 'styled-components'

export function ArticleLink({
  publisherDomain,
  articleId,
  title,
  lead,
}: {
  publisherDomain: string
  articleId: string
  title?: string
  lead: string
}) {
  return (
    <a
      href={`https://www.${publisherDomain}/${articleId}`}
      target='_parent'
      className='item'
    >
      <i className='chevron right icon' />
      <div className='content'>
        <div className='header'>{title}</div>
        {lead}
      </div>
    </a>
  )
}

export function ArticleBlock({
  header,
  articleId,
  title,
  lead,
  publisherDomain = '24heures.ch',
}: {
  header: string
  articleId: string
  title?: string
  lead: string
  publisherDomain?: string
}) {
  return (
    <>
      <h4>{header}</h4>
      <div className='ui icon very infographic list'>
        <ArticleLink
          publisherDomain={publisherDomain}
          articleId={articleId}
          title={title}
          lead={lead}
        />
      </div>
    </>
  )
}

/**
 * Dynamic inline teaser Function.
 * This function allows to render one or several article teasers.
 *
 * @param {string} title The title displayed on the bloc containing the teasers. "Lire aussi:" is the default value.
 * @param {string[]} ids The list of articles' ids.
 */
export const ArticleTeaser = ({
  title = 'Lire aussi:',
  ids,
  color = null,
  pub = '24heures.ch',
}: {
  title?: string
  ids: string[]
  color?: string | null
  pub?: string
}) => {
  const [articles, setArticles] = useState(
    new Map<string | number, ArticleData>()
  )

  useEffect(() => {
    setArticles(new Map<string | number, ArticleData>())
  }, [ids])

  useEffect(() => {
    if (articles.size === 0) {
      ids.forEach((id) => {
        try {
          getArticleTeaserData(pub, id).then(
            (articleTeaserData: ArticleData) => {
              if (articleTeaserData) {
                console.log(articleTeaserData)
                setArticles(
                  (articles) => new Map(articles.set(id, articleTeaserData))
                )
              }
            }
          )
        } catch (err) {
          console.warn(err)
        }
      })
    }
  }, [articles, ids, pub])

  return (
    <>
      {articles.size > 0 && (
        <Container>
          <Header>{title}</Header>
          <div>
            {[...articles.values()].map((article: ArticleData) => (
              <TeaserLink key={`article-teaser-${article.id}`}>
                <a
                  href={article.url}
                  target='_blank'
                  rel='noreferrer'
                  style={{ textDecoration: 'none' }}
                >
                  <ImageContainer>
                    <Image alt='' src={article.imageSrc} />
                  </ImageContainer>
                  <div>
                    <ArticleHeader>
                      {article.isPremium && (
                        <Abo style={{ backgroundColor: color || 'inherit' }}>
                          ABO
                        </Abo>
                      )}
                      {article.titleHeader}
                    </ArticleHeader>
                    <ArticleTitle>{article.title}</ArticleTitle>
                    <ArticleDate>
                      {new Date(article.publishingDate).toLocaleDateString(
                        'fr-FR',
                        {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        }
                      )}
                      {/* <time
                        dateTime='1702567834000
'
                      ></time> */}
                    </ArticleDate>
                  </div>
                </a>
              </TeaserLink>
            ))}
          </div>
        </Container>
      )}
    </>
  )
}

const Header = styled.div`
  line-height: 1.15em;
  font-weight: 800;
  padding-bottom: 1em;
  font-size: 1em;
`

const Container = styled.div`
  font-size: 1em;
  font-family: var(--font-plex);
`
const TeaserLink = styled.div`
  a {
    display: flex;
    flex-direction: row;
    padding: 0 0 0 1em;
    border-left: 3px solid #c8c8d1;
    margin-bottom: 1em;

    @media screen and (max-width: 768px) {
      flex-direction: column;
    }

    &:link,
    &:visited {
      color: rgba(0, 0, 0, 0.75);
    }

    &:hover {
      color: rgba(0, 0, 0, 0.85);
    }
  }
`

const ImageContainer = styled.div`
  width: 100%;
  max-width: 145px;
  height: auto;
  margin-right: 1em;
  overflow: hidden;
  position: relative;

  &:after {
    content: '';
    display: block;
    padding-bottom: 56%;
  }

  @media screen and (max-width: 768px) {
    margin-bottom: 0.25em;
  }
`
const Image = styled.img`
  width: 100%;
  height: auto;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
`

const ArticleHeader = styled.div`
  font-size: 0.75em;
  color: var(--text-color);
  font-weight: bold;
`
const ArticleTitle = styled.div`
  font-size: 0.9em;
  font-weight: 600;
  line-height: 1.4em;
  margin-top: 0.1em;
  color: var(--text-color);
`
const ArticleDate = styled.div`
  font-size: 0.75em;
  color: var(--text-color);
  margin-top: 0.5em;
`

const Abo = styled.span`
  display: inline-block;
  font-size: 0.65em;
  line-height: 1.25em;
  padding: 0.3em 0.5em 0.12em;
  background-color: var(--primary);
  color: #fff;
  margin-right: 0.65em;
`
